// Hosted (RAG) agent subscription — shown to a creator right after their
// agent's first knowledge-base upload, so they can subscribe the agent to a
// hosting plan. Real Razorpay payment: order -> Checkout.js -> verified
// confirm, same pattern as the existing comment-unlock flow (lib/razorpay.ts).
import { useState } from 'react'
import {
  useGetAgentSubscriptionPlansQuery,
  useGetAgentSubscriptionQuery,
  useGetAgentSubscriptionInvoicesQuery,
  useCreateAgentSubscriptionOrderMutation,
  useConfirmAgentSubscriptionPaymentMutation,
} from '../../store/api/agentSubscriptionsApi'
import { openRazorpayCheckout } from '../../lib/razorpay'
import { useToast } from '../../context/ToastContext'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { useAppSelector } from '../../hooks/useAppDispatch'
import s from '../../styles/pages/DeveloperPage.module.css'

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra',
  'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
]

function fmtRupees(paise: number) {
  return `₹${(paise / 100).toLocaleString('en-IN')}`
}
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function AgentSubscriptionModal({ agentId, onClose, onPaid }: {
  agentId: string
  onClose: () => void
  // Fired once the payment is verified and the subscription is active, before
  // the modal closes — lets the caller resume whatever the paywall interrupted
  // (e.g. finish the knowledge-file upload the creator had started).
  onPaid?: () => void
}) {
  const { data, isLoading } = useGetAgentSubscriptionPlansQuery(agentId)
  const { data: subData } = useGetAgentSubscriptionQuery(agentId)
  const { data: invoiceData } = useGetAgentSubscriptionInvoicesQuery(agentId)
  const toast = useToast()
  const [createOrder] = useCreateAgentSubscriptionOrderMutation()
  const [confirmPayment] = useConfirmAgentSubscriptionPaymentMutation()
  const { accessToken } = useAppSelector(st => st.auth)

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [buyerState, setBuyerState] = useState('')
  const [buyerGstin, setBuyerGstin] = useState('')
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [openedId, setOpenedId] = useState<string | null>(null)

  const plans = data?.plans ?? []
  const selectedPlan = plans.find(p => p.id === selectedPlanId)
  const invoices = invoiceData?.invoices ?? []
  const sub = subData?.subscription ?? null
  const isRecovery = sub?.status === 'grace' || sub?.status === 'deactivated'

  const recoveryDaysLeft = (() => {
    if (sub?.status === 'grace' && sub.graceEndDate) {
      return Math.max(0, Math.ceil((new Date(sub.graceEndDate).getTime() - Date.now()) / 864e5))
    }
    if (sub?.status === 'deactivated' && sub.deactivatedAt) {
      return Math.max(0, Math.ceil((new Date(sub.deactivatedAt).getTime() + 30 * 864e5 - Date.now()) / 864e5))
    }
    return null
  })()

  const handleSubscribe = async () => {
    if (!selectedPlanId || !selectedPlan) return
    if (!buyerState) { setError('Please select your state for the tax invoice.'); return }
    setError(null)
    setPaying(true)
    try {
      const order = await createOrder({ agentId, planId: selectedPlanId }).unwrap()

      const payment = await openRazorpayCheckout({
        keyId: order.keyId,
        orderId: order.orderId,
        amount: order.amount,
        currency: order.currency,
        description: `${selectedPlan.name} hosting plan`,
      })

      await confirmPayment({
        agentId,
        planId: selectedPlanId,
        razorpayOrderId: payment.razorpayOrderId,
        razorpayPaymentId: payment.razorpayPaymentId,
        razorpaySignature: payment.razorpaySignature,
        buyerState,
        buyerGstin: buyerGstin.trim() || undefined,
      }).unwrap()

      toast.success(`Payment of ₹${(order.amount / 100).toLocaleString('en-IN')} received — ${selectedPlan.name} hosting plan is active. Your invoice is in Settings → Billing.`)
      onPaid?.()
      onClose()
    } catch (err: any) {
      if (err?.message === 'cancelled') {
        // user dismissed the payment modal — no-op, button re-enables via finally
      } else {
        setError(err?.data?.message ?? err?.message ?? 'Payment failed. Please try again.')
      }
    } finally {
      setPaying(false)
    }
  }

  const handleDownloadInvoice = async (invoiceId: string) => {
    // Opened synchronously, before the fetch — a tab opened after an await no
    // longer counts as "the user just clicked something" and gets silently
    // blocked in several browsers. Navigating an already-open blank tab once
    // the PDF arrives sidesteps that, and doubles as viewing the receipt.
    const win = window.open('', '_blank')
    setDownloadingId(invoiceId); setOpenedId(null)
    try {
      const { blob, filename } = await api.downloadFile('ai', ENDPOINTS.agentSubscriptions.invoiceDownload(agentId, invoiceId), accessToken)
      const url = URL.createObjectURL(blob)
      if (win) {
        win.location.href = url
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = filename ?? 'invoice.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      setOpenedId(invoiceId)
      setTimeout(() => setOpenedId(null), 4000)
    } catch {
      win?.close()
      setError('Could not open that invoice. Please try again shortly.')
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <div className={s.modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 460, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-primary)' }}>
            {isRecovery ? '⚠️ Reactivate this agent' : '🎉 Your agent has knowledge!'}
          </span>
          <button onClick={onClose} className={s.closeBtn}>✕</button>
        </div>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 0 }}>
          {isRecovery
            ? sub?.status === 'deactivated'
              ? `This agent has been deactivated. Pay now to bring it back${recoveryDaysLeft != null ? ` — ${recoveryDaysLeft} day${recoveryDaysLeft === 1 ? '' : 's'} left before it and its knowledge base are permanently removed` : ''}.`
              : `Your subscription has lapsed${recoveryDaysLeft != null ? ` — ${recoveryDaysLeft} day${recoveryDaysLeft === 1 ? '' : 's'} of grace left` : ''}. Renew now to keep this agent live.`
            : 'Subscribe to a hosting plan to keep this agent live and answering from your documents.'}
        </p>

        {isLoading && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Loading plans...</div>}

        {!isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', margin: '1rem 0' }}>
            {plans.map(plan => (
              <label
                key={plan.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.75rem 1rem', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${selectedPlanId === plan.id ? 'var(--accent-primary)' : 'var(--border)'}`,
                  background: selectedPlanId === plan.id ? 'rgba(124,58,237,0.08)' : 'var(--bg-elevated)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <input type="radio" name="plan" checked={selectedPlanId === plan.id} onChange={() => setSelectedPlanId(plan.id)} />
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{plan.name}</span>
                </div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  {fmtRupees(plan.priceInPaise)}
                </span>
              </label>
            ))}
            {plans.length === 0 && (
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No plans available yet.</div>
            )}
          </div>
        )}

        {/* Billing details for the tax invoice */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            State <span style={{ color: '#ef4444' }}>*</span>
            <select
              value={buyerState}
              onChange={e => setBuyerState(e.target.value)}
              style={{ width: '100%', marginTop: '0.2rem', padding: '0.4rem 0.5rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem' }}
            >
              <option value="">Select your state…</option>
              {INDIAN_STATES.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            GSTIN <span style={{ color: 'var(--text-muted)' }}>(optional — for input tax credit)</span>
            <input
              value={buyerGstin}
              onChange={e => setBuyerGstin(e.target.value.toUpperCase())}
              placeholder="22AAAAA0000A1Z5"
              maxLength={15}
              style={{ width: '100%', marginTop: '0.2rem', padding: '0.4rem 0.5rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem', fontFamily: 'monospace' }}
            />
          </label>
        </div>

        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0 0 0.75rem' }}>
          All prices are inclusive of GST. A tax invoice is generated after payment.
        </p>

        {error && <div style={{ fontSize: '0.78rem', color: '#ef4444', marginBottom: '0.75rem' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={onClose} className={s.ghostBtnSm}>Maybe later</button>
          <button onClick={() => void handleSubscribe()} disabled={!selectedPlanId || paying} className={s.primaryBtn}>
            {paying ? 'Processing...' : selectedPlan ? `Pay ${fmtRupees(selectedPlan.priceInPaise)}` : 'Choose a plan'}
          </button>
        </div>

        {/* Past invoices */}
        {invoices.length > 0 && (
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border)', paddingTop: '0.9rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Invoices</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {invoices.map(inv => (
                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem', padding: '0.4rem 0.5rem', background: 'var(--bg-elevated)', borderRadius: 6, border: '1px solid var(--border)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--text-primary)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.invoiceNumber}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.68rem' }}>{fmtDate(inv.issuedAt)} · {fmtRupees(inv.totalPaise)}{inv.isPlaceholder ? ' · SAMPLE' : ''}</div>
                    {openedId === inv.id && <div style={{ color: 'var(--success)', fontSize: '0.66rem', marginTop: '0.1rem' }}>✓ Opened in a new tab</div>}
                  </div>
                  <button
                    onClick={() => void handleDownloadInvoice(inv.id)}
                    disabled={downloadingId === inv.id}
                    className={s.ghostBtnSm}
                    style={{ fontSize: '0.7rem', flexShrink: 0 }}
                  >
                    {downloadingId === inv.id ? 'Opening…' : '👁 View / Download'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
